import { QueryClient, keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import {
  AchRepository,
  AchTransfer,
  AdminRepository,
  AdminTransfer,
  BookRepository,
  BookTransfer,
  CreateAchTransfer,
  CreateBookTransfer,
  CreateFXQuoteRequest,
  CreateIntlWireTransfer,
  CreateRealtimeTransfer,
  CreateWireTransfer,
  CryptoRepository,
  CryptoTransfer,
  FxQuote,
  FxRateSheet,
  IntlWireRepository,
  IntlWireTransfer,
  RealtimeRepository,
  RealtimeTransfer,
  ReviewWireTransferRequest,
  TransferFilterParams,
  TransferListResponse,
  TransferRepository,
  WireDrawdownRepository,
  WireRepository,
  WireTransfer,
  transfers,
} from '~/repositories';
import { CheckRepository, CheckTransfer } from '~/repositories/Transfer/CheckRepository';
import { useSessionStore } from '~/stores/Session';
import { ApiError, UnifiedTransfer, WireDrawdownRequest } from '~/typings/API';

import { RepositoryMethod, createApiHook } from './utils/createApiHook';

export type TransferID =
  | 'ach'
  | 'wire'
  | 'book'
  | 'intl-wire'
  | 'check'
  | 'realtime'
  | 'wire-drawdown'
  | 'admin'
  | 'crypto';
export type TransferType =
  | AchTransfer
  | WireTransfer
  | BookTransfer
  | IntlWireTransfer
  | CheckTransfer
  | RealtimeTransfer
  | WireDrawdownRequest
  | AdminTransfer
  | CryptoTransfer;
type TransferResponse = (params: GetRequestType) => Promise<TransferType>;

export type TransferFilterType = TransferFilterParams & {
  page?: number;
};

const TransferMethods = new Map<TransferID, TransferResponse>([
  ['ach', AchRepository.get],
  ['wire', WireRepository.get],
  ['book', BookRepository.get],
  ['intl-wire', IntlWireRepository.get],
  ['check', CheckRepository.get],
  ['realtime', RealtimeRepository.get],
  ['wire-drawdown', WireDrawdownRepository.get],
  ['admin', AdminRepository.get],
  ['crypto', CryptoRepository.get],
]);

export const useTransfer = <T>(type: TransferID, triggerAutomatically: boolean = true) =>
  createApiHook<T, GetRequestType>(TransferMethods.get(type) as RepositoryMethod<T, GetRequestType>, {
    triggerAutomatically,
    triggerOnSessionStoreSubscribe: triggerAutomatically,
  });

export const useTransfersFetch = createApiHook<TransferListResponse, TransferFilterType>(TransferRepository.getAll);

export const transfersQueryKeys = {
  all: ['transfers'] as const,
  list: (platformId: string | undefined, isSandbox: boolean | undefined, params?: TransferFilterType) =>
    [...transfersQueryKeys.all, 'list', platformId, isSandbox ?? false, params] as const,
};

export const useTransfers = (params?: TransferFilterType) => {
  const { currentPlatform, isSandbox } = useSessionStore();
  const platformId = currentPlatform?.id;

  return useQuery({
    queryKey: transfersQueryKeys.list(platformId, isSandbox, params),
    queryFn: () => TransferRepository.getAll(params),
    enabled: !!platformId,
    staleTime: 15000,
    placeholderData: keepPreviousData,
  });
};

const transferTypeMatches = (filterType: string | undefined, transferType: string | undefined): boolean => {
  if (!filterType) return true;
  if (!transferType) return false;
  const typeMap: Record<string, string[]> = {
    achCredit: ['ach_credit'],
    achDebit: ['ach_debit'],
    book: ['book'],
    check: ['check_credit', 'check_debit'],
    swift: ['swift'],
    wire: ['wire'],
    realtime: ['realtime'],
  };
  return typeMap[filterType]?.includes(transferType) ?? false;
};

export const optimisticallyAddTransfer = (
  queryClient: QueryClient,
  platformId: string | undefined,
  isSandbox: boolean | undefined,
  newTransfer: Partial<UnifiedTransfer> & { id: string }
) => {
  if (typeof newTransfer.amount !== 'number' || isNaN(newTransfer.amount)) {
    return;
  }
  if (typeof newTransfer.status !== 'string' || newTransfer.status.length === 0) {
    return;
  }

  const queries = queryClient.getQueriesData<TransferListResponse>({
    queryKey: transfersQueryKeys.all,
  });

  const fullTransfer: UnifiedTransfer = {
    currencyCode: 'USD',
    description: '',
    isIncoming: false,
    ...newTransfer,
    createdAt: newTransfer.createdAt instanceof Date ? newTransfer.createdAt : new Date(),
  } as UnifiedTransfer;

  const normalizedIsSandbox = isSandbox ?? false;

  queries.forEach(([queryKey, data]) => {
    if (!data) return;

    const keyPlatformId = queryKey[2];
    const keyIsSandbox = queryKey[3];
    const keyParams = queryKey[4] as TransferFilterType | undefined;

    if (keyPlatformId !== platformId || keyIsSandbox !== normalizedIsSandbox) {
      return;
    }

    if (keyParams?.type && !transferTypeMatches(keyParams.type, fullTransfer.type)) {
      return;
    }

    if (keyParams?.bankAccountId) {
      const transferBankAccountIds = [
        fullTransfer.senderInternalAccount?.bankAccountId,
        fullTransfer.receiverInternalAccount?.bankAccountId,
      ].filter(Boolean);
      if (!transferBankAccountIds.includes(keyParams.bankAccountId)) {
        return;
      }
    }

    const existingTransfers = data.transfers.filter((t) => t.id !== fullTransfer.id);
    queryClient.setQueryData<TransferListResponse>(queryKey, {
      ...data,
      transfers: [fullTransfer, ...existingTransfers],
      totalResultsCount:
        existingTransfers.length < data.transfers.length ? data.totalResultsCount : data.totalResultsCount + 1,
    });
  });
};

export const useOptimisticTransferAdd = () => {
  const queryClient = useQueryClient();
  const { currentPlatform, isSandbox } = useSessionStore();
  const platformId = currentPlatform?.id;

  return useCallback(
    (newTransfer: Partial<UnifiedTransfer> & { id: string }) => {
      optimisticallyAddTransfer(queryClient, platformId, isSandbox, newTransfer);
    },
    [queryClient, platformId, isSandbox]
  );
};

export const useCreateACHTransfer = createApiHook<AchTransfer, CreateAchTransfer>(AchRepository.create);

export const useCreateWireTransfer = createApiHook<WireTransfer, CreateWireTransfer>(WireRepository.create);

export const useCreateBookTransfer = createApiHook<BookTransfer, CreateBookTransfer>(BookRepository.create);

export const useCreateIntlWireTransfer = createApiHook<IntlWireTransfer, CreateIntlWireTransfer>(
  IntlWireRepository.create
);

interface CheckTransferIdProps {
  onSuccess: (id: string) => void;
  onError: (message: string) => void;
}

export const useCheckTransferId = ({ onSuccess, onError }: CheckTransferIdProps) => {
  const handleSuccess = useCallback(
    (data: TransferType) => {
      const id = 'id' in data ? data.id : 'adminTransferId' in data ? data.adminTransferId : undefined;
      if (typeof id !== 'undefined') {
        onSuccess(id);
      }
    },
    [onSuccess]
  );

  const handleError = useCallback(
    (error: ApiError) => {
      console.info('Filter by ID', { error });

      if (onError) {
        onError('Please enter a valid Transfer ID');
      }
    },
    [onError]
  );

  const { createRequest: requestAchTransfer } = useTransfer<AchTransfer>(
    'ach',
    false
  )({ onSuccess: handleSuccess, onError: handleError });
  const { createRequest: requestWireTransfer } = useTransfer<WireTransfer>(
    'wire',
    false
  )({ onSuccess: handleSuccess, onError: handleError });
  const { createRequest: requestBookTransfer } = useTransfer<BookTransfer>(
    'book',
    false
  )({ onSuccess: handleSuccess, onError: handleError });
  const { createRequest: requestIntlWireTransfer } = useTransfer<IntlWireTransfer>(
    'intl-wire',
    false
  )({ onSuccess: handleSuccess, onError: handleError });
  const { createRequest: requestCheckTransfer } = useTransfer<CheckTransfer>(
    'check',
    false
  )({ onSuccess: handleSuccess, onError: handleError });
  const { createRequest: requestWireDrawdown } = useTransfer<WireDrawdownRequest>(
    'wire-drawdown',
    false
  )({ onSuccess: handleSuccess, onError: handleError });
  const { createRequest: requestCryptoTransfer } = useTransfer<CryptoTransfer>(
    'crypto',
    false
  )({ onSuccess: handleSuccess, onError: handleError });

  const checkTransferId = useCallback(
    (id: string) => {
      if (
        !transfers.some((transfer) => transfer.idString && id.startsWith(transfer.idString)) &&
        !id.startsWith('admt_') &&
        !id.startsWith('crtr_')
      ) {
        onError('Enter a valid Transfer ID');

        return;
      }

      if (id.startsWith('acht_')) {
        requestAchTransfer({ id });
      } else if (id.startsWith('wire_')) {
        requestWireTransfer({ id });
      } else if (id.startsWith('wdrw_')) {
        requestWireDrawdown({ id });
      } else if (id.startsWith('book_')) {
        requestBookTransfer({ id });
      } else if (id.startsWith('chkt_')) {
        requestCheckTransfer({ id });
      } else if (id.startsWith('swft_')) {
        requestIntlWireTransfer({ id });
      } else if (id.startsWith('crtr_')) {
        requestCryptoTransfer({ id });
      }
    },
    [
      requestAchTransfer,
      requestWireTransfer,
      requestBookTransfer,
      requestIntlWireTransfer,
      requestCheckTransfer,
      requestWireDrawdown,
      requestCryptoTransfer,
      onError,
    ]
  );

  return checkTransferId;
};

export const useFxRateSheet = createApiHook<FxRateSheet, unknown>(IntlWireRepository.getFxRateSheet);

export const useCreateFxQuote = createApiHook<FxQuote, CreateFXQuoteRequest>(IntlWireRepository.createFxQuote);

export const useCreateRealtimeTransfer = createApiHook<RealtimeTransfer, CreateRealtimeTransfer>(
  RealtimeRepository.create
);

export const useReviewAchTransfer = createApiHook<AchTransfer, ReviewWireTransferRequest>(AchRepository.review);

export const useReviewWireTransfer = createApiHook<WireTransfer, ReviewWireTransferRequest>(WireRepository.review);
